/**
 * Persona UDV: portal (paciente) vs clinical (médico).
 * Contrato: docs/CONTRATO-UDV-PERSONAS.md
 */
import {
  INVITE_QUERY_KEY,
  PERSONA_QUERY_KEY,
  STORAGE_KEYS,
  UDV_PERSONAS,
  UDV_PLANS,
} from './constants'
import {
  parseInviteFromSearchParams,
  parsePersonaFromSearchParams,
  parseTicketFromSearchParams,
  parseElevationTokenFromSearchParams,
} from './parsePlanQuery'
import { loadEntitlements } from './entitlements'
import { getTicketClinicalEntitlements } from './clinicalElevation'
import { isDeveloperMode } from './resolveAccess'
import { matchPlatformElevationSession } from './platformElevationSession'

function canUseStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage
  } catch (e) {
    return false
  }
}

/**
 * @returns {'portal'|'clinical'|null}
 */
export function getStoredPersona() {
  if (!canUseStorage()) return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEYS.PERSONA)
    if (v === UDV_PERSONAS.PORTAL || v === UDV_PERSONAS.CLINICAL) return v
  } catch (e) {
    /* ignore */
  }
  return null
}

/**
 * @param {'portal'|'clinical'|null|undefined} persona
 */
export function setStoredPersona(persona) {
  if (!canUseStorage()) return
  try {
    if (persona === UDV_PERSONAS.PORTAL || persona === UDV_PERSONAS.CLINICAL) {
      window.localStorage.setItem(STORAGE_KEYS.PERSONA, persona)
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * Detección + persistencia.
 * Orden: ticket → invite/persona=clinical → entitlements → stored.
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   entitlements?: object|null,
 *   persist?: boolean,
 * }} [opts]
 * @returns {'portal'|'clinical'|null}
 */
export function resolvePersona(opts = {}) {
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()
  const persist = opts.persist !== false

  const ticketId = parseTicketFromSearchParams(params)
  if (ticketId) {
    const clinical = getTicketClinicalEntitlements(ent)
    if (clinical) {
      if (persist) setStoredPersona(UDV_PERSONAS.CLINICAL)
      return UDV_PERSONAS.CLINICAL
    }
    if (matchPlatformElevationSession(ticketId)) {
      if (persist) setStoredPersona(UDV_PERSONAS.CLINICAL)
      return UDV_PERSONAS.CLINICAL
    }
    // elev en URL: canje async pendiente — no persistir portal (cold start).
    if (parseElevationTokenFromSearchParams(params)) {
      return UDV_PERSONAS.CLINICAL
    }
    if (persist) setStoredPersona(UDV_PERSONAS.PORTAL)
    return UDV_PERSONAS.PORTAL
  }

  const inviteId = parseInviteFromSearchParams(params)
  const personaQ = parsePersonaFromSearchParams(params)
  // Invite clínico siempre. ?persona=clinical solo en lab (prod: no elevar por query).
  if (inviteId || (personaQ === UDV_PERSONAS.CLINICAL && isDeveloperMode())) {
    if (persist) setStoredPersona(UDV_PERSONAS.CLINICAL)
    return UDV_PERSONAS.CLINICAL
  }
  if (personaQ === UDV_PERSONAS.PORTAL) {
    if (persist) setStoredPersona(UDV_PERSONAS.PORTAL)
    return UDV_PERSONAS.PORTAL
  }

  if (ent && ent.plan && ent.source !== 'ticket') {
    if (persist) setStoredPersona(UDV_PERSONAS.CLINICAL)
    return UDV_PERSONAS.CLINICAL
  }

  return getStoredPersona()
}

/**
 * ¿Perfil portal paciente? (chrome patient + default PREVIEW).
 * @param {{ persona?: string|null, plan?: string|null }} [opts]
 * @returns {boolean}
 */
export function isPortalProfile(opts = {}) {
  if (opts.plan === UDV_PLANS.PATIENT) return true
  if (opts.persona === UDV_PERSONAS.PORTAL) return true
  return false
}

/**
 * ¿Mostrar UI de login / AuthGateway?
 * Solo persona clinical (invite / entitlements clínicos).
 * Portal y cold-start genérico: nunca. Developer lab: sí, salvo portal.
 * @param {{ persona?: string|null, plan?: string|null, developer?: boolean }} [opts]
 */
export function shouldShowClinicalAuth(opts = {}) {
  const persona = opts.persona || getStoredPersona()
  if (persona === UDV_PERSONAS.PORTAL || opts.plan === 'patient') return false
  if (persona === UDV_PERSONAS.CLINICAL) return true
  if (opts.developer) return true
  return false
}

export { INVITE_QUERY_KEY, PERSONA_QUERY_KEY, UDV_PERSONAS }
